/**
 * Lokales Kalenderdatum aus einem `Date` — bewusst NICHT über `toISOString()`.
 *
 * Mantines Datumsauswahl (`DatePickerInput`) liefert ein `Date`-Objekt zur
 * LOKALEN Mitternacht des gewählten Tags. `toISOString()` rechnet dagegen
 * nach UTC: In einer Zeitzone vor UTC (z. B. MESZ, UTC+2) rutscht lokale
 * Mitternacht dabei auf den Vortag 22:00 Uhr UTC zurück — ein anschließendes
 * `.slice(0, 10)` schneidet dann das falsche, einen Tag zu frühe Datum heraus.
 * Diese Funktion liest stattdessen die lokalen Kalenderfelder direkt über
 * `getFullYear`/`getMonth`/`getDate`.
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Kehrfunktion zu `toLocalDateString`: baut aus "JJJJ-MM-TT" ein `Date` zur
 * lokalen Mitternacht — zum Vorbelegen einer Datumsauswahl mit einem bereits
 * gespeicherten ISO-Datum, symmetrisch zur Serialisierung oben (nicht über
 * `new Date(iso)`, das den String als UTC-Mitternacht interpretiert).
 */
export function fromLocalDateString(iso: string): Date {
  const parts = iso.split("-").map(Number);
  const year = parts[0] ?? 1970;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(year, month - 1, day);
}

/** Anzeigeformat aller Datumsfelder (Theme-Vorgabe für `DateInput`). */
export const GERMAN_DATE_FORMAT = "DD.MM.YYYY";

/**
 * ISO-Datum → deutsche Anzeige (Befund U7).
 *
 * Ampel-Badges und die Wartungsliste zeigten "2027-06-30", der Rest des
 * Werkzeugs "30.06.2027" — dasselbe Datum in zwei Schreibweisen auf einem
 * Bildschirm. Diese Funktion ist die eine Stelle dafür; die Anzeige wird
 * deutsch, GESPEICHERT und über die API übertragen wird weiter ISO.
 *
 * Bewusst ohne `Date`: Ein Kalenderdatum aus der Datenbank ("2027-06-30")
 * hat keine Uhrzeit und keine Zeitzone. Der Umweg über `new Date(iso)`
 * (UTC-Mitternacht) und `toLocaleDateString` würde es in Zeitzonen hinter
 * UTC auf den Vortag verschieben — dieselbe Falle wie in Befund B1, nur in
 * die andere Richtung. Umsortieren der Ziffern kann das nicht passieren.
 */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  return `${match[3]}.${match[2]}.${match[1]}`;
}

const berlinDatum = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/**
 * ZEITPUNKT (ISO mit Uhrzeit, UTC) → deutsches Kalenderdatum in Europe/Berlin.
 *
 * Gegenstück zu `formatIsoDate` für echte Zeitpunkte wie `closedAt`: Dort
 * würde das Abschneiden der ersten zehn Zeichen das UTC-Datum zeigen — ein
 * Abschluss zwischen 00:00 und 02:00 Uhr Berliner Zeit erschiene dann am
 * Vortag. Die Zeitzone ist fest gesetzt, nicht die des Browsers.
 */
export function formatBerlinDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : berlinDatum.format(d);
}

const berlinZeitpunkt = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** ZEITPUNKT (ISO, UTC) → „TT.MM.JJJJ, HH:MM:SS“ in Europe/Berlin. */
export function formatBerlinDateTime(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : berlinZeitpunkt.format(d);
}

/**
 * Wartungsende in seiner tatsächlichen Genauigkeit (Befund U7).
 *
 * Quartalsgenaue Enden bleiben "Q4/2026" — ein auf den Tag ausformuliertes
 * Datum würde eine Genauigkeit vortäuschen, die die Quelle nie genannt hat
 * (Spec §5). Tagesgenaue Enden werden deutsch angezeigt. Stand vorher
 * zweimal von Hand geschrieben (LifecycleBadge, SoftwareCatalogTab).
 */
export function formatMaintenanceEnd(
  iso: string | null | undefined,
  granularity: "tag" | "quartal" | null,
): string {
  if (!iso) return "–";
  if (granularity === "quartal") {
    const [year, month] = iso.split("-");
    return `Q${Math.ceil(Number(month) / 3)}/${year}`;
  }
  return formatIsoDate(iso);
}

/**
 * Parser für GETIPPTE Datumseingaben (Befund U5).
 *
 * Mantines `DateInput` erlaubt Tastatureingabe und Kalenderklick am selben
 * Feld — der Kalenderklick liefert ein `Date` zur LOKALEN Mitternacht. Damit
 * beide Wege buchstäblich dasselbe ergeben, baut dieser Parser das `Date`
 * ebenfalls über `new Date(jahr, monat, tag)` (lokale Mitternacht) statt über
 * `new Date(string)`, das "2027-06-30" als UTC-Mitternacht liest und in
 * Zeitzonen vor UTC auf den Vortag zurückrutscht — genau die Falle, gegen die
 * `toLocalDateString` gebaut wurde.
 *
 * Ohne eigenen Parser fiele `DateInput` auf `dayjs(wert, "DD.MM.YYYY")`
 * zurück; dayjs wertet den Formatstring aber nur mit dem Plugin
 * `customParseFormat` aus, das dieses Projekt nicht lädt — "30.06.2027" würde
 * also gar nicht erkannt. Akzeptiert wird deshalb hier ausdrücklich beides:
 * das deutsche Format (30.06.2027, auch 30.6.27) und ISO (2027-06-30), weil
 * das Katalogfeld vorher ein natives `type="date"`-Feld war und Anwender
 * dort ISO getippt haben.
 *
 * Kalendarisch ungültige Eingaben (31.02.2027) ergeben `null` statt
 * stillschweigend in den Folgemonat überzulaufen.
 */
export function parseGermanDate(value: string): Date | null {
  const trimmed = value.trim();

  const german = /^(\d{1,2})\s*[.\-/]\s*(\d{1,2})\s*[.\-/]\s*(\d{2}|\d{4})$/.exec(trimmed);
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);

  let year: number;
  let month: number;
  let day: number;

  if (german) {
    day = Number(german[1]);
    month = Number(german[2]);
    const rawYear = Number(german[3]);
    // Zweistellige Jahre: 27 → 2027. Wartungsenden liegen in der Zukunft,
    // ein Jahrhundertwechsel-Fenster wäre hier Theorie ohne Anwendungsfall.
    year = rawYear < 100 ? 2000 + rawYear : rawYear;
  } else if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else {
    return null;
  }

  const parsed = new Date(year, month - 1, day);
  // `new Date(2027, 1, 31)` läuft still auf den 3. März über — der
  // Rückvergleich der lokalen Kalenderfelder fängt das ab.
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}
